import { Injectable } from '@angular/core';
import { Announcement } from '@app/core/interfaces/c5/announcement';
import { RoutingGroup } from '@app/core/interfaces/c5/routing-group';
import { SiteWithLocations } from '@app/core/interfaces/site-with-locations';
import { RoutingGroupsRepository } from '@app/core/repositories/routing-groups.repository';
import { SiteRepository } from '@app/core/repositories/site.repository';
import { Cached, cachedGroup } from '@app/utils/cached';
import { wrapError } from '@app/utils/rxjs';
import { CachedHelperService } from '../cached-helper.service';

@Injectable({
  providedIn: 'root',
})
export class SiteContextService {
  constructor(
    private siteRepository: SiteRepository,
    private routingGroupsRepository: RoutingGroupsRepository,
    private cachedHelperService: CachedHelperService
  ) {}

  private _siteWithLocations: Cached<SiteWithLocations> | null = null;
  get siteWithLocations(): Cached<SiteWithLocations> {
    this._siteWithLocations ??= this.cachedHelperService.routeAwareCached({
      clearDelay: 30000,
      dataProducer: ([, , siteName]) =>
        this.siteRepository
          .getSiteWithLocations(siteName)
          .pipe(wrapError('site-context-service:siteWithLocations')),
      id: ([serviceProviderId, enterpriseId, siteName]) =>
        `site-context->${serviceProviderId}:${enterpriseId}:${siteName}`,
      routeParameters: ['serviceProviderId', 'enterpriseId', 'siteName'],
    });

    return this._siteWithLocations;
  }

  private _siteAnnouncements: Cached<Announcement[]> | null = null;
  get siteAnnouncements(): Cached<Announcement[]> {
    this._siteAnnouncements ??= cachedGroup(
      [this.siteWithLocations],
      ([siteWithLocations]) => {
        return this.siteRepository.listAnnouncementsForSite(siteWithLocations.site.id);
      },
      {
        id: `site-context:announcements`,
      }
    );

    return this._siteAnnouncements;
  }

  private _siteRoutingGroups: Cached<RoutingGroup[]> | null = null;
  get siteRoutingGroups(): Cached<RoutingGroup[]> {
    this._siteRoutingGroups ??= cachedGroup(
      [this.siteWithLocations],
      ([siteWithLocations]) => {
        return this.routingGroupsRepository.listForSite(siteWithLocations.site.id);
      },
      {
        id: `site-context:routingGroups`,
      }
    );

    return this._siteRoutingGroups;
  }
}
